import Bugsnag from '@bugsnag/js';

// Sanitized session telemetry. Dimensions never include tenant names,
// subjects, tokens, or payloads - only which invariant fired and where
// (transport kind and URL path pattern). Scope violations are migration
// canary signals; ambient-use warnings audit the supported console and any
// unexplained compatibility callers (see the lifecycle policies).
export type SessionTelemetryCode =
  | 'organization_scope_missing' // a scoped operation reached the transport without a tenant scope
  | 'organization_scope_invalid' // a tenant scope carried a value that cannot be an organization header
  | 'organization_scope_mismatch' // a valid tenant scope conflicted with the committed organization
  | 'legacy_ambient_scope_used' // console/compatibility request resolved a tenant from the live URL
  | 'write_signal_rejected' // a caller attempted to attach cancellation to a durable write
  | 'session_transition_replay_rejected' // a 401 replay was abandoned because the session changed mid-refresh
  | 'stale_publication_suppressed'; // a publication surface dropped a stale-scope result

type SessionTelemetryEvent = {
  code: SessionTelemetryCode;
  transport?: 'api' | 'channelApi' | 'fetch';
  // Structural path only; literal segments and query/hash values are never emitted.
  path?: string;
};

export const sanitizeTelemetryPath = (path: string | undefined): string | undefined => {
  if (path === undefined) return undefined;

  let pathname: string;
  try {
    pathname = new URL(path, 'https://session-telemetry.invalid').pathname;
  } catch {
    pathname = path.split(/[?#]/, 1)[0];
  }

  const segmentCount = pathname.split(/[\\/]/).filter(Boolean).length;
  return segmentCount === 0 ? '/' : `/${Array.from({ length: segmentCount }, () => ':segment').join('/')}`;
};

export const emitSessionTelemetry = (event: SessionTelemetryEvent): void => {
  const payload = { code: event.code, transport: event.transport, path: sanitizeTelemetryPath(event.path) };
  try {
    if (!Bugsnag.isStarted()) return;
    Bugsnag.leaveBreadcrumb('session-telemetry', payload, 'state');
    // Scope violations are fail-closed programming errors. Ambient console
    // use is an audit warning. Both must be visible beyond breadcrumbs.
    if (
      event.code === 'organization_scope_missing' ||
      event.code === 'organization_scope_invalid' ||
      event.code === 'organization_scope_mismatch' ||
      event.code === 'write_signal_rejected' ||
      event.code === 'legacy_ambient_scope_used'
    ) {
      Bugsnag.notify(new Error(`session-telemetry:${event.code}`), (bugsnagEvent) => {
        if (event.code === 'legacy_ambient_scope_used') bugsnagEvent.severity = 'warning';
        bugsnagEvent.addMetadata('session_telemetry', payload);
      });
    }
  } catch {
    // Telemetry must never break the request path (Bugsnag.start may not
    // have run in unit tests or very early boot).
  }
};
